import { A, useLocation } from "@solidjs/router";
import { createSignal, For, Show } from "solid-js";

const links = [
  { href: "/pricing", label: "Pricing" },
  { href: "/benchmarks", label: "Benchmarks" },
  { href: "/calculator", label: "Calculator" },
  { href: "/docs", label: "Docs" },
  { href: "/blog", label: "Blog" },
  { href: "/releases", label: "Releases" },
  { href: "/models", label: "Models" },
];

/** Face-cropped gorilla mascot (`logo.svg`). Kept as `AnvilMark` so existing
 *  importers stay unchanged. Default is large enough to read the face. */
export function AnvilMark(props: { class?: string }) {
  return (
    <img
      src="/logo.svg"
      alt=""
      class={`object-contain ${props.class ?? "h-14 w-14"}`}
      aria-hidden="true"
    />
  );
}

export default function Nav() {
  const [open, setOpen] = createSignal(false);
  const location = useLocation();

  return (
    <header class="sticky top-0 z-40 border-b border-seam bg-iron-1/95 backdrop-blur">
      <div class="mx-auto flex min-h-[72px] max-w-6xl items-center gap-6 px-5 py-2">
        <A href="/" class="flex items-center gap-3 no-underline shrink-0" onClick={() => setOpen(false)}>
          <AnvilMark class="h-16 w-16" />
          <div class="flex flex-col leading-snug">
            <span class="font-disp text-[13px] sm:text-[14px] font-bold uppercase tracking-[0.14em] text-ink">
              Throughput Lines
            </span>
            <span class="font-disp text-[13px] sm:text-[14px] font-bold uppercase tracking-[0.14em] text-ink">
              Of Code Per Day
            </span>
          </div>
        </A>

        <nav class="hidden flex-1 items-center justify-end gap-1 md:flex">
          <For each={links}>
            {(l) => {
              const active =
                location.pathname === l.href ||
                (l.href !== "/" && location.pathname.startsWith(l.href + "/"));
              return (
              <A
                href={l.href}
                class={`border px-4 py-2 font-disp text-[11px] font-semibold uppercase tracking-wide2 no-underline transition-colors ${
                  active
                    ? "border-seam bg-iron-2 text-forge-hi"
                    : "border-transparent text-ink-dim hover:text-ink"
                }`}
              >
                {l.label}
              </A>
              );
            }}
          </For>
          <A
            href="/login?mode=signin"
            class="btn-forge-outline ml-3 !py-2"
          >
            Sign in
          </A>
          <A
            href="/login?mode=signup"
            class="btn-forge ml-2 !py-2"
          >
            Sign up
          </A>
        </nav>

        <button
          class="ml-auto flex h-9 w-9 items-center justify-center border border-seam text-ink-dim md:hidden"
          aria-label="Toggle navigation"
          onClick={() => setOpen(!open())}
        >
          <span class="font-disp text-lg leading-none">{open() ? "×" : "≡"}</span>
        </button>
      </div>

      <Show when={open()}>
        <nav class="border-t border-seam bg-iron-1 md:hidden">
          <For each={links}>
            {(l) => {
              const active =
                location.pathname === l.href ||
                (l.href !== "/" && location.pathname.startsWith(l.href + "/"));
              return (
              <A
                href={l.href}
                onClick={() => setOpen(false)}
                class={`block border-l-2 px-5 py-3 font-disp text-[12px] font-semibold uppercase tracking-wide2 no-underline ${
                  active
                    ? "border-forge bg-iron-2 text-forge-hi"
                    : "border-transparent text-ink-dim"
                }`}
              >
                {l.label}
              </A>
              );
            }}
          </For>
          <div class="grid grid-cols-2 gap-2 px-5 py-3 border-t border-seam">
            <A
              href="/login?mode=signin"
              onClick={() => setOpen(false)}
              class="btn-forge-outline block text-center !py-2"
            >
              Sign in
            </A>
            <A
              href="/login?mode=signup"
              onClick={() => setOpen(false)}
              class="btn-forge block text-center !py-2"
            >
              Sign up
            </A>
          </div>
        </nav>
      </Show>
    </header>
  );
}
